import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import WeekCalendar, { type LessonRow, type GroupInfo } from './components/WeekCalendar';
import LessonList from './components/LessonList';
import LessonDetailModal from './components/LessonDetailModal';

type ViewMode = 'hafta' | 'liste';

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getWeekDates(anchor: Date): Date[] {
  const day = anchor.getDay();
  const diffToMonday = (day + 6) % 7;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - diffToMonday);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

function weekRangeLabel(days: Date[]): string {
  const first = days[0];
  const last = days[6];
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
  const f = first.toLocaleDateString('tr-TR', opts);
  const l = last.toLocaleDateString('tr-TR', { ...opts, year: 'numeric' });
  return `${f} - ${l}`;
}

export default function Program() {
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [groupMap, setGroupMap] = useState<Record<string, GroupInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('hafta');
  const [groupFilter, setGroupFilter] = useState<string>('hepsi');
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const [selectedLesson, setSelectedLesson] = useState<LessonRow | null>(null);

  const todayYmd = toYmd(new Date());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: lessonsData, error: lessonsError }, { data: groupsData, error: groupsError }] =
        await Promise.all([
          supabase.from('zehrahoca_lessons').select('*').order('date', { ascending: true }),
          supabase.from('zehrahoca_groups').select('*').eq('active', true),
        ]);

      if (lessonsError) throw lessonsError;
      if (groupsError) throw groupsError;

      const map: Record<string, GroupInfo> = {};
      (groupsData ?? []).forEach((g: { id: string; name: string; color: string | null }) => {
        map[g.id] = { name: g.name, color: g.color ?? '#94a3b8' };
      });

      setLessons((lessonsData ?? []) as LessonRow[]);
      setGroupMap(map);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleChange = () => fetchAll();
    window.addEventListener('zehrahoca:lessons-changed', handleChange);
    return () => {
      window.removeEventListener('zehrahoca:lessons-changed', handleChange);
    };
  }, [fetchAll]);

  const weekDays = useMemo(() => getWeekDates(weekAnchor), [weekAnchor]);

  const lessonsByDate = useMemo(() => {
    const map: Record<string, LessonRow[]> = {};
    lessons.forEach((l) => {
      if (!l.date) return;
      if (!map[l.date]) map[l.date] = [];
      map[l.date].push(l);
    });
    return map;
  }, [lessons]);

  const groupOptions = useMemo(
    () => Object.entries(groupMap).sort((a, b) => a[1].name.localeCompare(b[1].name)),
    [groupMap],
  );

  const filteredLessons = useMemo(() => {
    if (groupFilter === 'hepsi') return lessons;
    return lessons.filter((l) => l.group_id === groupFilter);
  }, [lessons, groupFilter]);

  const selectedGroup = selectedLesson?.group_id ? groupMap[selectedLesson.group_id] : undefined;

  function shiftWeek(delta: number) {
    setWeekAnchor((prev) => {
      const d = new Date(prev);
      d.setDate(prev.getDate() + delta * 7);
      return d;
    });
  }

  function goToday() {
    setWeekAnchor(new Date());
  }

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Ders programı yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Ders programı yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button
            type="button"
            onClick={fetchAll}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ders Programı</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Planladığınız dersleri takvim üzerinde görüntüleyin ve canlı derslere erişin.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('zehrahoca:open-lesson-modal'))}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line text-base" />
          Yeni Ders Ekle
        </button>
      </div>

      {/* Filtre ve görünüm kontrolü */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setViewMode('hafta')}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
              viewMode === 'hafta' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            <i className="ri-calendar-2-line mr-1.5" /> Hafta
          </button>
          <button
            type="button"
            onClick={() => setViewMode('liste')}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition cursor-pointer ${
              viewMode === 'liste' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            <i className="ri-list-check-3 mr-1.5" /> Liste
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="rounded-lg border border-background-300 bg-white px-3 py-2 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
          >
            <option value="hepsi">Tüm Gruplar</option>
            {groupOptions.map(([id, info]) => (
              <option key={id} value={id}>{info.name}</option>
            ))}
          </select>
        </div>
      </div>

      {viewMode === 'hafta' && (
        <>
          {/* Hafta navigasyonu */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => shiftWeek(-1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-300 bg-white text-foreground-600 transition hover:bg-background-100 cursor-pointer"
                aria-label="Önceki hafta"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                type="button"
                onClick={goToday}
                className="whitespace-nowrap rounded-lg border border-background-300 bg-white px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
              >
                Bugün
              </button>
              <button
                type="button"
                onClick={() => shiftWeek(1)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-300 bg-white text-foreground-600 transition hover:bg-background-100 cursor-pointer"
                aria-label="Sonraki hafta"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
            <p className="font-heading text-base font-semibold text-foreground-900">{weekRangeLabel(weekDays)}</p>
          </div>

          <WeekCalendar
            weekDays={weekDays}
            lessonsByDate={lessonsByDate}
            groupMap={groupMap}
            todayYmd={todayYmd}
            onSelectLesson={setSelectedLesson}
          />
        </>
      )}

      {viewMode === 'liste' && (
        <LessonList lessons={filteredLessons} groupMap={groupMap} onSelectLesson={setSelectedLesson} />
      )}

      <LessonDetailModal
        open={selectedLesson !== null}
        onClose={() => setSelectedLesson(null)}
        lesson={selectedLesson}
        group={selectedGroup}
        onChanged={fetchAll}
      />
    </div>
  );
}